import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Bot, User, Briefcase, Users, Calendar, Wallet, FileText, Headphones } from "lucide-react";
import { PageHeader, SectionCard } from "@/components/app/PageKit";

const EXAMPLES = [
  { icon: Users,    label: "Who is on leave next week?" },
  { icon: Calendar, label: "Summarize pending approvals by team" },
  { icon: Briefcase,label: "Which projects are at risk?" },
  { icon: FileText, label: "Draft an experience letter for an employee" },
  { icon: Wallet,   label: "Explain my payslip deductions" },
  { icon: Headphones, label: "List employees with pending asset returns" },
];

const SHORTCUTS = [
  { label: "Attendance",  to: "/attendance" },
  { label: "Leaves",      to: "/leaves" },
  { label: "Payroll",     to: "/payroll" },
  { label: "Approvals",   to: "/approvals" },
  { label: "Documents",   to: "/documents" },
];

export default function AIAssistant() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);
  const loc = useLocation();

  useEffect(() => { if (loc.state?.prompt) setText(loc.state.prompt); }, [loc.state]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [msgs, busy]);

  const send = async (e, override) => {
    e?.preventDefault();
    const message = (override ?? text).trim();
    if (!message || busy) return;
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that right now." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden flex flex-col" data-testid="ai-page" style={{ minHeight: "calc(100vh - 64px)" }}>
      <PageHeader
        category="AI Workspace"
        title="Qrew AI Assistant"
        description="Tenant-aware copilot grounded in your live HR data — people, attendance, leaves, projects, payroll & helpdesk."
        badge="Beta"
      />

      <div className="grid lg:grid-cols-[minmax(0,1fr)_280px] gap-5 flex-1 min-h-0">
        {/* CHAT COLUMN */}
        <Card className="flex flex-col border-slate-200 shadow-sm bg-white overflow-hidden min-h-[480px]">
          <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-gradient-to-b from-slate-50/40 to-white">
            {msgs.length === 0 && (
              <div className="max-w-2xl mx-auto py-8">
                <div className="text-center mb-5">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl qh-grad-bg text-white mb-3">
                    <Sparkles className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold font-[Outfit] text-slate-900">How can I help today?</h3>
                  <p className="text-[13px] text-slate-500 mt-1">Ask anything about your team, payroll, leaves or approvals — I'm grounded in your tenant's live data.</p>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Suggested HR questions</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {EXAMPLES.map((q) => (
                    <button
                      key={q.label}
                      onClick={(e) => send(e, q.label)}
                      data-testid={`ai-example-${q.label.slice(0, 10).replace(/\s+/g, "-")}`}
                      className="text-left p-3 rounded-xl border border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 text-[13px] transition group flex items-start gap-2.5"
                    >
                      <q.icon className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
                      <span className="text-slate-700 group-hover:text-slate-900">{q.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-2xl mx-auto flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-slate-900 text-white" : "qh-grad-bg text-white"}`}>
                  {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`p-3.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {busy && (
              <div className="max-w-2xl mx-auto flex gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center qh-grad-bg text-white"><Bot className="w-4 h-4" /></div>
                <div className="p-3.5 rounded-2xl bg-white border border-slate-200 flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form onSubmit={send} className="p-4 border-t border-slate-200 bg-white">
            <div className="max-w-2xl mx-auto flex gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Ask QrewHR anything — e.g., 'Who is on leave this week?'"
                className="focus-visible:ring-violet-500 h-11"
                data-testid="ai-input"
              />
              <button
                type="submit"
                disabled={busy}
                className="qh-btn btn-primary qh-btn-icon"
                style={{ width: 44, height: 44 }}
                data-testid="ai-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center">Powered by Claude Sonnet 4.5 · grounded in your tenant data</p>
          </form>
        </Card>

        {/* RIGHT RAIL */}
        <div className="space-y-4">
          <SectionCard icon={Sparkles} title="Tenant aware" sub="Real-time grounding">
            <p className="text-[12px] text-slate-600 leading-relaxed">
              Every reply is grounded in your workspace's live data — counts, leave requests, open tasks, ticket queues — so you never get hallucinated answers.
            </p>
          </SectionCard>
          <SectionCard title="Module shortcuts" sub="Jump anywhere">
            <div className="grid grid-cols-2 gap-2">
              {SHORTCUTS.map((s) => (
                <a key={s.to} href={s.to} className="px-3 py-2 rounded-lg bg-slate-50 hover:bg-violet-50 text-[12px] font-semibold text-slate-700 hover:text-violet-700 border border-slate-100 hover:border-violet-200 transition text-center">
                  {s.label}
                </a>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
